import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Quote, Clock, FileCheck, Activity } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSEO, breadcrumbJsonLd, articleJsonLd, faqPageJsonLd, siteUrl } from "@/lib/seo";
import CTASection from "@/components/CTASection";

const PHOTO_BEFORE = "/images/case-demo-before.jpg";
const PHOTO_AFTER = "/images/case-demo-after.jpg";

const PATH = "/van-demo-tot-digitale-wagenparkcontrole";

// FAQ items (NL/EN) — embedded into FAQPage JSON-LD for rich results.
const FAQS = [
  {
    id: "faq-1",
    q_nl: "Wat houdt mobiele truckwash op locatie precies in?",
    a_nl:
      "Onze mobiele service-units komen naar uw vestiging, distributiecentrum of standplaats en reinigen vrachtwagens en bestelbussen ter plaatse. Het wagenpark staat niet stil voor wasstraat-bezoek en de planning blijft op koers.",
    q_en: "What does mobile truckwash on-site actually involve?",
    a_en:
      "Our mobile service units come to your depot, distribution centre or yard and clean trucks and vans on site. The fleet doesn't stand idle for wash-bay visits and the planning stays on track.",
  },
  {
    id: "faq-2",
    q_nl: "Hoe werkt HACCP-conforme vrachtwagenreiniging bij foodtransport?",
    a_nl:
      "Voor levensmiddelentransport gebruiken wij een HACCP-werkwijze voor het interieur: gecontroleerde reinigingsstappen, geschikte middelen en registratie per kenteken. Het bijbehorende reinigingscertificaat is direct na de wasbeurt digitaal beschikbaar in WaszBase.",
    q_en:
      "How does HACCP-compliant truck cleaning work for food transport?",
    a_en:
      "For food transport we use a HACCP workflow on the interior: controlled cleaning steps, suitable agents and per-license-plate registration. The cleaning certificate is available digitally in WaszBase right after the wash.",
  },
  {
    id: "faq-3",
    q_nl: "Hoe helpt realtime voertuigstatus de planning bij spoedritten?",
    a_nl:
      "In WaszBase zien planners welke voertuigen gereinigd, vrijgegeven en inzetbaar zijn. Dat voorkomt dat een chauffeur naar een voertuig wordt gestuurd dat nog op de wasvloer staat — minder onnodige stilstand en minder miscommunicatie binnen de logistieke planning.",
    q_en:
      "How does real-time vehicle status help dispatch during urgent rides?",
    a_en:
      "In WaszBase planners see which vehicles are cleaned, released and operational. That prevents drivers being sent to a vehicle that is still on the wash floor — less unnecessary downtime and less miscommunication in the planning.",
  },
];

const PhotoCard = ({ src, alt, caption, dataTestid, objectPosition = "center" }) => (
  <figure
    data-testid={dataTestid}
    className="my-12 mx-auto max-w-md rounded-2xl overflow-hidden border border-white/10 bg-[#0F0F23]"
  >
    <div className="aspect-[3/4] w-full bg-black/40">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        style={{ objectPosition }}
        className="w-full h-full object-cover"
      />
    </div>
    {caption && (
      <figcaption className="px-6 py-4 text-xs text-white/45 italic border-t border-white/5">
        {caption}
      </figcaption>
    )}
  </figure>
);

const Section = ({ eyebrow, title, children }) => (
  <section className="mb-16 sm:mb-20 last:mb-0">
    {eyebrow && <p className="section-overline mb-3">{eyebrow}</p>}
    {title && (
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white leading-[1.15] mb-6">
        {title}
      </h2>
    )}
    <div className="text-white/70 text-base sm:text-[17px] leading-[1.75] space-y-5">
      {children}
    </div>
  </section>
);

const KeyValueRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/20 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-[#00D4FF]" strokeWidth={1.8} />
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold">
        {label}
      </div>
      <div className="text-sm text-white/85 font-medium mt-0.5">{value}</div>
    </div>
  </div>
);

const CaseDemoNaarDigitaleControle = () => {
  const { lang } = useLang();

  const title = lang === "nl"
    ? "Van Demo tot Digitale Wagenparkcontrole"
    : "From Demo to Digital Fleet Control";

  const metaTitle = lang === "nl"
    ? "Van Demo tot Digitale Wagenparkcontrole | Praktijkcase | Wasz.nl"
    : "From Demo to Digital Fleet Control | Case Study | Wasz.nl";

  const metaDesc = lang === "nl"
    ? "Praktijkverhaal van een transportbedrijf dat overstapte op Wasz.nl: drie demovoertuigen, een vaste wekelijkse planning en — vele jaren later — realtime wagenparkinzicht via WaszBase."
    : "Real-world story of a transport company switching to Wasz.nl: three demo vehicles, a fixed weekly schedule and — years later — real-time fleet visibility via WaszBase.";

  const datePublished = "2026-05-27";
  const canonical = siteUrl(PATH);

  useSEO({
    title: metaTitle,
    description: metaDesc,
    canonical,
    image: siteUrl(PHOTO_AFTER),
    lang,
    jsonLd: [
      {
        id: "case-breadcrumb",
        data: breadcrumbJsonLd([
          { name: lang === "nl" ? "Home" : "Home", url: siteUrl("/") },
          { name: lang === "nl" ? "Praktijkcases" : "Case studies", url: siteUrl("/blog") },
          { name: title, url: canonical },
        ]),
      },
      {
        id: "case-article",
        data: articleJsonLd(
          {
            slug: PATH.replace(/^\//, ""),
            title_nl: title,
            title_en: "From Demo to Digital Fleet Control",
            excerpt_nl: metaDesc,
            excerpt_en: metaDesc,
            image: PHOTO_AFTER,
            date: datePublished,
          },
          lang,
        ),
      },
      {
        id: "case-faq",
        data: faqPageJsonLd(FAQS, lang),
      },
    ],
  });

  // ===== NL content =====
  if (lang === "nl") {
    return (
      <main data-testid="case-demo-page" className="min-h-screen">
        {/* HERO */}
        <section className="relative pt-16 pb-12 sm:pt-24 sm:pb-16 overflow-hidden">
          <div className="absolute inset-0 -z-10 cyber-grid opacity-15" />
          <div className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full bg-[#00D4FF]/6 blur-3xl -z-10" />

          <div className="mx-auto max-w-4xl px-6 lg:px-8">
            <Link
              to="/blog"
              data-testid="case-back-link"
              className="text-xs text-white/50 hover:text-[#00D4FF] inline-flex items-center gap-1.5 mb-6"
            >
              <ArrowLeft className="w-3 h-3" /> Terug naar kennisbank
            </Link>

            <p className="section-overline mb-4">Praktijkcase · Foodtransport</p>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
              Van Demo tot{" "}
              <span className="text-[#00D4FF]">Digitale Wagenparkcontrole</span>
            </h1>

            <p className="mt-7 text-lg sm:text-xl text-white/65 leading-relaxed max-w-3xl">
              Hoe één demo van drie voertuigen leidde tot een meerjarige samenwerking — en uiteindelijk tot realtime wagenparkinzicht via WaszBase.
            </p>

            {/* Meta strip */}
            <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-5">
              <KeyValueRow icon={Clock} label="Looptijd" value="Meerjarige samenwerking" />
              <KeyValueRow icon={FileCheck} label="Scope" value="HACCP-gerelateerd foodtransport" />
              <KeyValueRow icon={Activity} label="Resultaat" value="Realtime status via WaszBase" />
            </div>
          </div>
        </section>

        {/* BODY */}
        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-3xl px-6 lg:px-8">

            {/* Opening */}
            <Section eyebrow="De aanleiding" title="Soms zegt één voertuig al genoeg.">
              <p>
                Jaren geleden kregen wij de kans om een transportbedrijf over te nemen van een andere mobiele truckwashleverancier. Zoals altijd wilden wij eerst zelf zien hoe de voertuigen erbij stonden voordat we ergens een oordeel over gaven. Toen de achterdeuren van de eerste wagen opengingen, keken wij elkaar direct aan.
              </p>
              <p>
                <strong className="text-white">Volgens de klant was dit voertuig ongeveer één maand eerder nog gereinigd.</strong>
              </p>
              <p>
                Voor ons was dat moeilijk te begrijpen. Zeker binnen foodtransport en HACCP-gerelateerde logistiek hoort een laadruimte er na één maand gebruik nooit zo uit te zien wanneer een{" "}
                <Link to="/vrachtwagenreiniging" className="text-[#00D4FF] hover:underline underline-offset-4">
                  vrachtwagenreiniging
                </Link>{" "}
                echt grondig en structureel wordt uitgevoerd.
              </p>
            </Section>

            <PhotoCard
              src={PHOTO_BEFORE}
              alt="Vervuilde laadruimte van een bedrijfsvoertuig, foto gemaakt vóór de eerste demoreiniging door Wasz.nl"
              caption="Situatie zoals wij die aantroffen — laadruimte met aanslag, vlekken en restanten op de vloer en wanden. Volgens de klant was het voertuig ongeveer een maand eerder gereinigd."
              dataTestid="case-photo-before"
              objectPosition="65% center"
            />

            {/* The demo */}
            <Section eyebrow="De demo" title="Drie voertuigen. Drie keer het verschil laten zien.">
              <p>
                Wij stelden daarom voor om drie voertuigen volledig te reinigen als demonstratie, zodat het transportbedrijf zelf het verschil kon ervaren in aanpak, kwaliteit en resultaat.
              </p>
              <p>
                Na het eerste voertuig zag de klant direct verschil. Na het tweede voertuig werden de gesprekken serieuzer. En toen het derde voertuig volledig gereinigd was binnen onze{" "}
                <Link to="/mobiele-truckwash" className="text-[#00D4FF] hover:underline underline-offset-4">
                  mobiele truckwashaanpak
                </Link>
                , gaf de wagenparkbeheerder direct aan dat wij het volledige wagenpark mochten gaan onderhouden.
              </p>
              <p>
                Niet alleen vanwege het eindresultaat, maar vooral vanwege de combinatie van kwaliteit, snelheid en structuur. De voertuigen konden snel weer ingezet worden zonder dat er werd ingeleverd op reinigingsniveau of voedselveiligheid.
              </p>
            </Section>

            <PhotoCard
              src={PHOTO_AFTER}
              alt="Dezelfde laadruimte volledig schoon en strak afgeleverd na een mobiele HACCP-reiniging door Wasz.nl"
              caption="Hetzelfde compartiment na een complete mobiele reiniging — schoon, strak afgewerkt en gereed voor foodtransport."
              dataTestid="case-photo-after"
              objectPosition="65% center"
            />

            {/* Pull quote */}
            <div
              data-testid="case-pull-quote"
              className="my-14 sm:my-16 relative border-l-2 border-[#00D4FF] pl-6 sm:pl-8 py-2"
            >
              <Quote className="absolute -left-3 -top-2 w-6 h-6 text-[#00D4FF]/30 rotate-180 bg-[#050509]" strokeWidth={1.5} />
              <p className="text-xl sm:text-2xl text-white font-semibold leading-snug">
                "Niet alleen vanwege het eindresultaat, maar vooral vanwege de combinatie van kwaliteit, snelheid en structuur."
              </p>
              <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold">
                Wat de wagenparkbeheerder doorgaf
              </p>
            </div>

            {/* Weekly planning */}
            <Section eyebrow="De samenwerking" title="Een vaste wekelijkse planning.">
              <p>
                Kort daarna maakten wij samen een vaste wekelijkse planning voor het complete wagenpark.
              </p>
              <p>
                In die periode bestond WaszBase nog niet. Alle reinigingscertificaten en HACCP-documentatie werden toen nog handmatig opgesteld en verwerkt. Dat kostte veel tijd, maar gaf ons tegelijkertijd veel inzicht in wat transportbedrijven operationeel écht nodig hebben binnen dagelijkse logistieke processen.
              </p>
            </Section>

            {/* WaszBase evolution */}
            <Section eyebrow="De doorontwikkeling" title="Nu, vele jaren later, reinigen wij deze voertuigen nog steeds.">
              <p>
                Alleen is de manier waarop compleet doorontwikkeld.
              </p>
              <p>
                Via{" "}
                <Link to="/waszbase" className="text-[#00D4FF] hover:underline underline-offset-4">
                  WaszBase
                </Link>{" "}
                heeft het transportbedrijf tegenwoordig realtime inzicht in reinigingscertificaten, HACCP-documentatie en de actuele status van het volledige wagenpark. Direct na iedere vrachtwagenreiniging zijn certificaten digitaal beschikbaar en kunnen planners live meekijken welke voertuigen alweer inzetbaar zijn.
              </p>
              <p>
                Voor logistieke planners werkt dit bijzonder efficiënt.
              </p>
              <p>
                Bij spoedritten hoeft men niet meer te gokken welk voertuig beschikbaar is. Wanneer een chauffeur naar een voertuig gestuurd wordt dat toevallig nog op de wasvloer staat, kost dat onnodig tijd binnen de planning. Dankzij realtime inzicht in de status van het wagenpark kunnen planners direct zien welke voertuigen volledig gereinigd, vrijgegeven en inzetbaar zijn voor transport.
              </p>
              <p>
                Dat bespaart tijd, voorkomt miscommunicatie en vermindert onnodige stilstand binnen dagelijkse transport- en logistieke processen.
              </p>
            </Section>

            {/* Closing */}
            <Section eyebrow="Wat dit verhaal laat zien" title="Meedenken met de volledige operatie eromheen.">
              <p>
                Als wij terugkijken op deze samenwerking, laat het precies zien waar Wasz.nl voor staat.
              </p>
              <p>
                Niet alleen professionele mobiele truckwash en vrachtwagenreiniging op locatie.
              </p>
              <p>
                <strong className="text-white">Maar meedenken met de volledige operatie eromheen.</strong>
              </p>
            </Section>

            {/* Inline CTA — calm, professional */}
            <div
              data-testid="case-inline-cta"
              className="mt-20 rounded-2xl border border-white/10 bg-[#0F0F23] p-7 sm:p-10"
            >
              <p className="section-overline mb-3">Volgende stap</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-tight mb-4">
                Benieuwd hoe zo'n traject er bij u uitziet?
              </h2>
              <p className="text-white/65 leading-relaxed mb-7">
                Wij komen graag langs voor een rondleiding en een vrijblijvend demovoorstel op uw eigen voertuigen. Geen verkooppraat — wij laten gewoon zien hoe wij werken.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link to="/contact?type=demo" data-testid="case-cta-demo">
                  <button className="btn-primary">
                    Plan een demo <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <Link to="/mobiele-truckwash" data-testid="case-cta-mobiele">
                  <button className="btn-secondary">
                    Meer over mobiele truckwash
                  </button>
                </Link>
                <Link to="/waszbase" data-testid="case-cta-waszbase">
                  <button className="btn-secondary">Bekijk WaszBase</button>
                </Link>
              </div>
            </div>

            {/* Related quick-links */}
            <div className="mt-12 pt-8 border-t border-white/5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/45">
              <span className="uppercase tracking-[0.16em] text-white/35 font-semibold">
                Lees verder:
              </span>
              <Link to="/mobiele-truckwash" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">
                Mobiele truckwash op locatie
              </Link>
              <Link to="/vrachtwagenreiniging" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">
                Vrachtwagenreiniging
              </Link>
              <Link to="/haccp-certificaten" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">
                HACCP & certificaten
              </Link>
              <Link to="/waszbase" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">
                WaszBase klantportaal
              </Link>
              <Link to="/contact" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">
                Contact
              </Link>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
    );
  }

  // ===== EN content =====
  return (
    <main data-testid="case-demo-page" className="min-h-screen">
      <section className="relative pt-16 pb-12 sm:pt-24 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10 cyber-grid opacity-15" />
        <div className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full bg-[#00D4FF]/6 blur-3xl -z-10" />
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <Link to="/blog" className="text-xs text-white/50 hover:text-[#00D4FF] inline-flex items-center gap-1.5 mb-6">
            <ArrowLeft className="w-3 h-3" /> Back to knowledge base
          </Link>
          <p className="section-overline mb-4">Case study · Food transport</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
            From Demo to <span className="text-[#00D4FF]">Digital Fleet Control</span>
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-white/65 leading-relaxed max-w-3xl">
            How a three-vehicle demo grew into a multi-year partnership — and ultimately into real-time fleet visibility through WaszBase.
          </p>
          <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-5">
            <KeyValueRow icon={Clock} label="Duration" value="Multi-year partnership" />
            <KeyValueRow icon={FileCheck} label="Scope" value="HACCP-related food transport" />
            <KeyValueRow icon={Activity} label="Outcome" value="Real-time status via WaszBase" />
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <Section eyebrow="The trigger" title="Sometimes one vehicle says it all.">
            <p>
              Years ago we got the chance to take over a transport operator from another mobile truckwash provider. As always we wanted to see the vehicles ourselves before forming any opinion. When the rear doors of the first van opened, we looked at each other immediately.
            </p>
            <p>
              <strong className="text-white">According to the customer, this vehicle had been cleaned only about a month earlier.</strong>
            </p>
            <p>
              That was hard to understand. Especially in food transport and HACCP-related logistics, a cargo area should never look like that one month after a thorough{" "}
              <Link to="/vrachtwagenreiniging" className="text-[#00D4FF] hover:underline underline-offset-4">truck cleaning</Link>.
            </p>
          </Section>

          <PhotoCard
            src={PHOTO_BEFORE}
            alt="Soiled cargo area before the first demonstration cleaning by Wasz.nl"
            caption="The situation we found — cargo area with residues, stains and dirt on the floor and walls. According to the customer, the vehicle had been cleaned about a month earlier."
            objectPosition="65% center"
          />

          <Section eyebrow="The demo" title="Three vehicles. Three chances to show the difference.">
            <p>
              We proposed cleaning three vehicles as a demonstration so the operator could experience the difference in approach, quality and result for themselves.
            </p>
            <p>
              After the first vehicle the customer immediately saw the difference. After the second, conversations got more serious. And once the third vehicle was fully cleaned within our{" "}
              <Link to="/mobiele-truckwash" className="text-[#00D4FF] hover:underline underline-offset-4">mobile truckwash approach</Link>, the fleet manager said straight away we could take over the entire fleet.
            </p>
            <p>
              Not only because of the end result, but mostly because of the combination of quality, speed and structure. Vehicles could quickly be put back into service without compromising on cleaning level or food safety.
            </p>
          </Section>

          <PhotoCard
            src={PHOTO_AFTER}
            alt="The same cargo area fully cleaned and finished after a mobile HACCP cleaning by Wasz.nl"
            caption="The same compartment after a complete mobile cleaning — clean, neatly finished and ready for food transport."
            objectPosition="65% center"
          />

          <div className="my-14 sm:my-16 relative border-l-2 border-[#00D4FF] pl-6 sm:pl-8 py-2">
            <Quote className="absolute -left-3 -top-2 w-6 h-6 text-[#00D4FF]/30 rotate-180 bg-[#050509]" strokeWidth={1.5} />
            <p className="text-xl sm:text-2xl text-white font-semibold leading-snug">
              "Not just the end result, but the combination of quality, speed and structure."
            </p>
            <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold">
              What the fleet manager fed back
            </p>
          </div>

          <Section eyebrow="The partnership" title="A fixed weekly schedule.">
            <p>Shortly after, we put together a fixed weekly schedule covering the full fleet.</p>
            <p>
              At the time WaszBase didn't exist yet. All cleaning certificates and HACCP documentation were drawn up and processed by hand. It took time, but it also gave us a deep view of what transport operators actually need in their daily logistics processes.
            </p>
          </Section>

          <Section eyebrow="The evolution" title="Many years later, we still clean these vehicles.">
            <p>Only the way we do it has fully evolved.</p>
            <p>
              Through{" "}
              <Link to="/waszbase" className="text-[#00D4FF] hover:underline underline-offset-4">WaszBase</Link>{" "}
              the operator now has real-time visibility of cleaning certificates, HACCP documentation and the current status of the entire fleet. Right after every wash, certificates are digitally available and planners can see live which vehicles are operational again.
            </p>
            <p>For dispatch planners this works particularly well.</p>
            <p>
              During urgent rides nobody has to guess which vehicle is available. Sending a driver to a vehicle still on the wash floor costs unnecessary time. Thanks to real-time fleet status, planners see directly which vehicles are cleaned, released and ready for transport.
            </p>
            <p>
              That saves time, prevents miscommunication and reduces avoidable downtime in daily transport and logistics processes.
            </p>
          </Section>

          <Section eyebrow="What this story shows" title="Thinking along with the wider operation.">
            <p>Looking back at this partnership, it shows exactly what Wasz.nl stands for.</p>
            <p>Not only professional mobile truckwash and on-site cleaning.</p>
            <p>
              <strong className="text-white">But thinking along with the wider operation around it.</strong>
            </p>
          </Section>

          <div className="mt-20 rounded-2xl border border-white/10 bg-[#0F0F23] p-7 sm:p-10">
            <p className="section-overline mb-3">Next step</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-tight mb-4">
              Curious how this could work for your fleet?
            </h2>
            <p className="text-white/65 leading-relaxed mb-7">
              We're happy to visit for a walkthrough and a no-obligation demo on your own vehicles. No sales pitch — just a demonstration of how we work.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/contact?type=demo">
                <button className="btn-primary">
                  Schedule a demo <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/mobiele-truckwash">
                <button className="btn-secondary">More on mobile truckwash</button>
              </Link>
              <Link to="/waszbase">
                <button className="btn-secondary">Explore WaszBase</button>
              </Link>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/45">
            <span className="uppercase tracking-[0.16em] text-white/35 font-semibold">Read on:</span>
            <Link to="/mobiele-truckwash" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">Mobile truckwash on-site</Link>
            <Link to="/vrachtwagenreiniging" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">Truck cleaning</Link>
            <Link to="/haccp-certificaten" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">HACCP & certificates</Link>
            <Link to="/waszbase" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">WaszBase portal</Link>
            <Link to="/contact" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">Contact</Link>
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default CaseDemoNaarDigitaleControle;
